import { PageLoading, PageError } from '../../components/common/PageState';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Armchair,
  ChartColumn,
  ChevronDown,
  Crosshair,
  Download,
  Ellipsis,
  Eraser,
  FileJson,
  FileSpreadsheet,
  History,
  PencilRuler,
  Printer,
  UserMinus,
  UserPen,
  UserPlus,
  Users,
  UsersRound,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import EmployeeRoster from '../../components/floorplan/EmployeeRoster';
import FloorCanvas from '../../components/floorplan/FloorCanvas';
import FloorplanLayoutEditor from '../../components/floorplan/FloorplanLayoutEditor';
import { FLOORPLAN_AREA_TYPES } from '../../components/floorplan/areaAppearance';
import VersionSelector from '../../components/floorplan/VersionSelector';
import ActivityFeed from '../../components/floorplan/ActivityFeed';
import DeskAssignmentDialog from '../../components/floorplan/DeskAssignmentDialog';
import EmployeeDetailDialog, { type PlacementUpdatePayload } from '../../components/floorplan/EmployeeDetailDialog';
import SmartAssignDialog from '../../components/floorplan/SmartAssignDialog';
import StatsDialog from '../../components/floorplan/StatsDialog';
import { EmptyState, ErrorState, Kbd, PageHeader, SectionCard, useDelayedFlag } from '../../components/ui';
import {
  useActivateFloorplanVersionMutation,
  useApplyFloorplanAutoAssignMutation,
  useApplyFloorplanBatchAssignmentsMutation,
  useClearFloorplanLevelAssignmentsMutation,
  useClearFloorplanAssignmentMutation,
  useCreateFloorplanVersionMutation,
  useGetFloorplanActivityQuery,
  useGetFloorplanLayoutQuery,
  useGetFloorplanLayoutsQuery,
  useGetFloorplanRosterQuery,
  usePreviewFloorplanAutoAssignMutation,
  usePreviewNearbyTeamMoveMutation,
  useSaveFloorplanAssignmentMutation,
  useSaveFloorplanGeometryMutation,
} from '../../store/slices/floorplanSlice';
import { useGetEmployeesQuery, useUpdateEmployeeMutation } from '../../store/slices/employeeSlice';
import { useGetDepartmentsQuery } from '../../store/slices/departmentSlice';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { focusRing, sbg } from '../../theme/sbgTokens';
import {
  FloorplanAssignment,
  FloorplanAutoAssignPreview,
  FloorplanDesk,
  FloorplanEmployee,
  FloorplanLayoutDetail,
  FloorplanLevel,
} from '../../types/floorplan';
import {
  buildEmployeeHierarchyLookups,
  isEmployeeDescendant,
  resolveManagerEmployeeId,
} from '../../utils/employeeHierarchy';
import { formatCount, formatPercent, formatTime } from '../../utils/format';
import { isManagerLike } from '../../utils/roles';
import CanvasLegend from './floorplan-page/CanvasLegend';
import PersonSearch from './floorplan-page/PersonSearch';
import SelectionPanel from './floorplan-page/SelectionPanel';
import TeamHighlight, { type TeamHighlightOption } from './floorplan-page/TeamHighlight';
import { buildPrintHtml, sortDeskNumbers } from './floorplan-page/printPlan';
import { useConfirmDialog } from './floorplan-page/useConfirmDialog';

const DEFAULT_ZOOM = 0.95;
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
/** Toolbar controls share one height so the row lines up (search, selects, segmented floors, zoom). */
const TOOLBAR_CONTROL_HEIGHT = 34;
type AssignmentResult = { ok: true } | { ok: false; message: string };
/** Errors stay until dismissed; success and info notices close after 5 seconds. */
type Feedback = { type: 'success' | 'error' | 'info'; message: string };

const createDownload = (filename: string, content: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const buildOptimisticAssignment = (
  desk: FloorplanDesk,
  levelId: string,
  employee: FloorplanEmployee
): FloorplanAssignment => {
  const timestamp = new Date().toISOString();

  return {
    id: `optimistic-${desk.id}-${employee.id}-${timestamp}`,
    deskId: desk.id,
    employeeId: employee.id,
    createdAt: timestamp,
    updatedAt: timestamp,
    desk: {
      id: desk.id,
      key: desk.key,
      number: desk.number,
      levelId,
    },
    employee,
  };
};

const applyAssignmentOptimistically = (
  assignments: FloorplanAssignment[],
  deskById: Map<string, { desk: FloorplanDesk; levelId: string }>,
  deskId: string,
  employee: FloorplanEmployee
) => {
  const next = assignments.map((assignment) => ({
    ...assignment,
    desk: { ...assignment.desk },
    employee: { ...assignment.employee },
  }));
  const targetDesk = deskById.get(deskId);

  if (!targetDesk) {
    return next;
  }

  const sourceIndex = next.findIndex((assignment) => assignment.employeeId === employee.id);
  const targetIndex = next.findIndex((assignment) => assignment.deskId === deskId);

  if (targetIndex >= 0 && next[targetIndex].employeeId === employee.id) {
    return next;
  }

  if (sourceIndex >= 0 && targetIndex >= 0 && sourceIndex !== targetIndex) {
    const targetEmployee = next[targetIndex].employee;

    next[sourceIndex] = {
      ...next[sourceIndex],
      employeeId: targetEmployee.id,
      employee: targetEmployee,
      updatedAt: new Date().toISOString(),
    };

    next[targetIndex] = {
      ...next[targetIndex],
      deskId: targetDesk.desk.id,
      employeeId: employee.id,
      updatedAt: new Date().toISOString(),
      desk: {
        id: targetDesk.desk.id,
        key: targetDesk.desk.key,
        number: targetDesk.desk.number,
        levelId: targetDesk.levelId,
      },
      employee,
    };

    return next;
  }

  if (sourceIndex >= 0) {
    next[sourceIndex] = {
      ...next[sourceIndex],
      deskId: targetDesk.desk.id,
      updatedAt: new Date().toISOString(),
      desk: {
        id: targetDesk.desk.id,
        key: targetDesk.desk.key,
        number: targetDesk.desk.number,
        levelId: targetDesk.levelId,
      },
      employee,
    };

    return next;
  }

  if (targetIndex >= 0) {
    next[targetIndex] = {
      ...next[targetIndex],
      employeeId: employee.id,
      employee,
      updatedAt: new Date().toISOString(),
    };

    return next;
  }

  return [...next, buildOptimisticAssignment(targetDesk.desk, targetDesk.levelId, employee)];
};

const buildCsvExport = (layoutDetail: FloorplanLayoutDetail, assignments: FloorplanAssignment[]) => {
  const assignmentsByDeskId = new Map(assignments.map((assignment) => [assignment.deskId, assignment]));
  const rows = layoutDetail.layout.levels
    .flatMap((level) =>
      [...level.desks]
        .sort((left, right) => sortDeskNumbers(left.number, right.number))
        .map((desk) => {
          const assignment = assignmentsByDeskId.get(desk.id);
          const employee = assignment?.employee;

          return [
            level.name,
            desk.key,
            desk.number,
            employee?.name || '',
            employee?.email || '',
            employee?.teamName || '',
            employee?.department?.name || '',
          ];
        })
    );

  return [
    ['level', 'desk_key', 'desk_number', 'employee_name', 'employee_email', 'team', 'department'],
    ...rows,
  ]
    .map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))
    .join('\n');
};

/*
 * Skeleton desks carry a hairline so they read on the sheet in dark mode too,
 * where the skeleton fill sits close to the surface.
 */
const SKELETON_TILE_SX = { border: `1px solid ${sbg.border.default}`, boxSizing: 'border-box' } as const;

/**
 * Canvas-sized skeleton in the drawing's own shape: the floor sheet with a row of
 * rooms and pods of desks, at the canvas's real height. Revealed after 300ms so
 * quick loads never flash; until then the space is held, so nothing jumps.
 */
const CanvasSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box
      role="status"
      aria-label="Loading the floor plan…"
      aria-busy="true"
      sx={{
        boxSizing: 'border-box',
        p: 2,
        bgcolor: sbg.bg.surfaceSubtle,
        height: { xs: 460, md: 'clamp(520px, 66vh, 780px)' },
        overflow: 'hidden',
      }}
    >
      <span className="sbg-sr-only">Loading the floor plan…</span>
      <Box
        aria-hidden="true"
        sx={{
          visibility: visible ? 'visible' : 'hidden',
          boxSizing: 'border-box',
          height: '100%',
          p: { xs: 2, md: 3 },
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr)',
          gap: 3,
          overflow: 'hidden',
          bgcolor: sbg.bg.surface,
          border: `1px solid ${sbg.border.default}`,
          borderRadius: sbg.radius.md,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
          {[0, 1, 2, 3, 4, 5].map((room) => (
            <Skeleton key={room} variant="rounded" height={72} sx={{ ...SKELETON_TILE_SX, flex: '1 0 120px' }} />
          ))}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
            alignContent: 'start',
            gap: 3,
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: 18 }, (_, pod) => (
            <Box key={pod} sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75 }}>
              {[0, 1, 2, 3, 4, 5].map((desk) => (
                <Skeleton key={desk} variant="rounded" height={36} sx={SKELETON_TILE_SX} />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/** Zoom out · current zoom (resets) · zoom in, as one quiet pill. */
const ZoomControls: React.FC<{ zoom: number; onChange: (zoom: number) => void }> = ({ zoom, onChange }) => {
  const label = formatPercent(zoom * 100);
  return (
    <Box
      role="group"
      aria-label="Zoom"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: TOOLBAR_CONTROL_HEIGHT,
        boxSizing: 'border-box',
        p: 0,
        border: `1px solid ${sbg.border.default}`,
        borderRadius: sbg.radius.pill,
        bgcolor: sbg.bg.surface,
      }}
    >
      <Tooltip title="Zoom out">
        <span>
          <IconButton
            size="small"
            aria-label="Zoom out"
            onClick={() => onChange(Math.max(MIN_ZOOM, Number((zoom - ZOOM_STEP).toFixed(2))))}
            disabled={zoom <= MIN_ZOOM}
          >
            <ZoomOut />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Reset zoom">
        <Button
          variant="text"
          size="small"
          aria-label={`Reset zoom (${label})`}
          onClick={() => onChange(DEFAULT_ZOOM)}
          sx={{ minWidth: 52, px: 1, fontVariantNumeric: 'tabular-nums' }}
        >
          {label}
        </Button>
      </Tooltip>
      <Tooltip title="Zoom in">
        <span>
          <IconButton
            size="small"
            aria-label="Zoom in"
            onClick={() => onChange(Math.min(MAX_ZOOM, Number((zoom + ZOOM_STEP).toFixed(2))))}
            disabled={zoom >= MAX_ZOOM}
          >
            <ZoomIn />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const FloorPlan: React.FC = () => {
  const { profile } = useAuth();
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>('');
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedLevelKey, setSelectedLevelKey] = useState('');
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [localAssignments, setLocalAssignments] = useState<FloorplanAssignment[]>([]);
  const [selectedDeskId, setSelectedDeskId] = useState<string | null>(null);
  const [deskDialogOpen, setDeskDialogOpen] = useState(false);
  const [highlightedDesk, setHighlightedDesk] = useState<{ deskId: string; nonce: number } | null>(null);
  const [showActivity, setShowActivity] = useState(false);
  const [highlightedTeam, setHighlightedTeam] = useState<string | null>(null);
  // True only once the seating Realtime channel reports SUBSCRIBED; the header says "Live" only then.
  const [realtimeLive, setRealtimeLive] = useState(false);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [toolsAnchorEl, setToolsAnchorEl] = useState<null | HTMLElement>(null);
  // Keep the opening floor as a fallback; the editor owns its separate, protected draft.
  const [editingLevel, setEditingLevel] = useState<FloorplanLevel | null>(null);
  const [pendingDeskIds, setPendingDeskIds] = useState<Set<string>>(new Set());
  const [isClearingLevel, setIsClearingLevel] = useState(false);
  const [liveLabel, setLiveLabel] = useState('Live');
  const [contextMenu, setContextMenu] = useState<{ deskId: string; x: number; y: number } | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [employeeDetailOpen, setEmployeeDetailOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{
    teamName: string;
    levelId: string;
    deskIds: Set<string>;
    count: number;
  } | null>(null);
  const [smartAssignOpen, setSmartAssignOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [smartAssignPreview, setSmartAssignPreview] = useState<FloorplanAutoAssignPreview | null>(null);
  const hoverSwitchTimerRef = useRef<number | null>(null);
  const hoverLevelKeyRef = useRef<string | null>(null);
  const ignoreRealtimeUntilRef = useRef(0);
  const historyRef = useRef<HTMLDivElement | null>(null);
  // Latest seating, for handlers that resume after an awaited confirm dialog:
  // polling and Realtime keep updating state while the dialog is open.
  const localAssignmentsRef = useRef<FloorplanAssignment[]>([]);
  const serverAssignmentsRef = useRef<FloorplanAssignment[]>([]);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const { data: layouts = [], isLoading: layoutsLoading, error: layoutsError, refetch: refetchLayouts } = useGetFloorplanLayoutsQuery();
  const { data: roster = [], isLoading: rosterLoading, error: rosterError, refetch: refetchRoster } = useGetFloorplanRosterQuery();
  const { data: allEmployees = [] } = useGetEmployeesQuery(undefined, { skip: profile?.role !== 'admin' });
  const { data: departments = [] } = useGetDepartmentsQuery(undefined, { skip: profile?.role !== 'admin' });
  const layoutQueryArg = selectedLayoutId
    ? { layoutId: selectedLayoutId, versionId: selectedVersionId || undefined }
    : skipToken;
  const {
    data: layoutDetail,
    isFetching: layoutLoading,
    error: layoutError,
    // Bumps on every successful fetch, including polls that return identical data.
    fulfilledTimeStamp: layoutCheckedAt,
    refetch: refetchLayout,
  } = useGetFloorplanLayoutQuery(layoutQueryArg, {
    // Geometry SELECT remains admin-only in Supabase. Every viewing role refreshes
    // through the authorized backend, including when Realtime is unavailable.
    pollingInterval: 30_000,
    skipPollingIfUnfocused: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: activity = [], isLoading: activityLoading, error: activityError, refetch: refetchActivity } = useGetFloorplanActivityQuery(
    selectedLayoutId ? { layoutId: selectedLayoutId, limit: 15 } : skipToken
  );

  const [saveAssignment, { isLoading: assignmentSaving }] = useSaveFloorplanAssignmentMutation();
  const [clearAssignment, { isLoading: assignmentClearing }] = useClearFloorplanAssignmentMutation();
  const [clearLevelAssignments] = useClearFloorplanLevelAssignmentsMutation();
  const [applyBatchAssignments] = useApplyFloorplanBatchAssignmentsMutation();
  const [previewNearbyTeamMove] = usePreviewNearbyTeamMoveMutation();
  const [previewFloorplanAutoAssign, { isLoading: previewingSmartAssign }] = usePreviewFloorplanAutoAssignMutation();
  const [applyFloorplanAutoAssign, { isLoading: applyingSmartAssign }] = useApplyFloorplanAutoAssignMutation();
  const [createVersion, { isLoading: versionCreating }] = useCreateFloorplanVersionMutation();
  const [activateVersion, { isLoading: versionActivating }] = useActivateFloorplanVersionMutation();
  const [updateEmployee, { isLoading: updatingEmployeePlacement }] = useUpdateEmployeeMutation();
  const [saveGeometry] = useSaveFloorplanGeometryMutation();

  const handleSaveGeometry = async (draft: FloorplanLevel) => {
    if (profile?.role !== 'admin' || !draft.updatedAt || draft.id !== editingLevel?.id) {
      throw new Error('Reopen the layout editor to load the current floor revision.');
    }
    try {
      await saveGeometry({
        layoutId: selectedLayoutId,
        levelId: draft.id,
        geometry: {
          expectedUpdatedAt: draft.updatedAt,
          name: draft.name,
          width: draft.width,
          height: draft.height,
          desks: draft.desks,
          areas: draft.areas,
        },
      }).unwrap();
    } catch (failure: any) {
      // A concurrent save can beat the next refresh. Fetch its snapshot so the
      // editor can offer an explicit reload without throwing away this draft.
      if (failure?.status === 409) void refetchLayout();
      throw new Error(failure?.data?.error || failure?.data?.message || 'Couldn’t save the floor layout. Your changes are still here; try again in a minute.');
    }
    setEditingLevel(null);
    setSelectedDeskId(null);
    setSelectedGroup(null);
    setFeedback({ type: 'success', message: `${draft.name} layout saved. Existing desk assignments have been kept.` });
  };

  useEffect(() => {
    if (!selectedLayoutId || profile?.role !== 'admin') return;
    const geometryChannel = supabase
      .channel(`floorplan-geometry-${selectedLayoutId}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'floorplan_levels', filter: `layout_id=eq.${selectedLayoutId}`,
      }, () => { refetchLayout(); refetchLayouts(); refetchActivity(); })
      .subscribe();
    return () => { supabase.removeChannel(geometryChannel); };
  }, [selectedLayoutId, profile?.role, refetchLayout, refetchLayouts, refetchActivity]);

  useEffect(() => {
    if (!selectedLayoutId && layouts.length > 0) {
      setSelectedLayoutId(layouts[0].id);
    }
  }, [layouts, selectedLayoutId]);

  useEffect(() => {
    if (!layoutDetail) {
      return;
    }

    const availableVersionIds = new Set(layoutDetail.versions.map((version) => version.id));
    if (!selectedVersionId || !availableVersionIds.has(selectedVersionId)) {
      setSelectedVersionId(layoutDetail.selectedVersionId);
    }
  }, [layoutDetail, selectedVersionId]);

  useEffect(() => {
    if (!layoutDetail?.layout.levels.length) {
      return;
    }

    const availableLevelKeys = new Set(layoutDetail.layout.levels.map((level) => level.key));
    if (!selectedLevelKey || !availableLevelKeys.has(selectedLevelKey)) {
      setSelectedLevelKey(layoutDetail.layout.levels[0].key);
    }
  }, [layoutDetail, selectedLevelKey]);

  useEffect(() => {
    setLocalAssignments(layoutDetail?.assignments || []);
    setPendingDeskIds(new Set());
    serverAssignmentsRef.current = layoutDetail?.assignments || [];
  }, [layoutDetail]);

  useEffect(() => {
    localAssignmentsRef.current = localAssignments;
  }, [localAssignments]);

  useEffect(() => {
    if (!selectedLayoutId || !selectedVersionId) {
      return undefined;
    }

    const setSyncedLabel = () => {
      setLiveLabel('Synced');
      window.setTimeout(() => setLiveLabel('Live'), 2200);
    };

    const assignmentsChannel = supabase
      .channel(`floorplan-assignments-${selectedVersionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'floorplan_assignments',
          filter: `version_id=eq.${selectedVersionId}`,
        },
        async () => {
          if (Date.now() < ignoreRealtimeUntilRef.current) {
            return;
          }

          await Promise.all([refetchLayout(), refetchActivity()]);
          setSyncedLabel();
        }
      )
      .subscribe((status) => {
        const state = String(status);
        if (state === 'SUBSCRIBED') setRealtimeLive(true);
        else if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT' || state === 'CLOSED') setRealtimeLive(false);
      });

    const versionsChannel = supabase
      .channel(`floorplan-versions-${selectedLayoutId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'floorplan_versions',
          filter: `layout_id=eq.${selectedLayoutId}`,
        },
        async () => {
          if (Date.now() < ignoreRealtimeUntilRef.current) {
            return;
          }

          await refetchLayout();
          setSyncedLabel();
        }
      )
      .subscribe();

    return () => {
      setRealtimeLive(false);
      supabase.removeChannel(assignmentsChannel);
      supabase.removeChannel(versionsChannel);
    };
  }, [refetchActivity, refetchLayout, selectedLayoutId, selectedVersionId]);

  useEffect(() => {
    setSelectedGroup(null);
    setContextMenu(null);
  }, [selectedLayoutId, selectedVersionId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedGroup(null);
        setContextMenu(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const rosterById = useMemo(
    () => new Map(roster.map((employee) => [employee.id, employee])),
    [roster]
  );

  const activeEmployees = useMemo(
    () => allEmployees.filter((employee) => !employee.employmentStatus || employee.employmentStatus === 'active'),
    [allEmployees]
  );

  const employeeHierarchy = useMemo(
    () => buildEmployeeHierarchyLookups(activeEmployees),
    [activeEmployees]
  );

  const managerCandidateIds = useMemo(() => {
    const ids = new Set<string>();

    activeEmployees.forEach((employee) => {
      const managerId = resolveManagerEmployeeId(
        {
          managerUserId: employee.managerUserId,
          managerName: employee.managerName,
        },
        employeeHierarchy
      );

      if (managerId) {
        ids.add(managerId);
      }
    });

    departments.forEach((department) => {
      const departmentManagerId = resolveManagerEmployeeId(
        {
          managerUserId: department.managerUserId,
          managerName: department.manager,
        },
        employeeHierarchy
      );

      if (departmentManagerId) {
        ids.add(departmentManagerId);
      }
    });

    return ids;
  }, [activeEmployees, departments, employeeHierarchy]);

  const managerCandidates = useMemo(
    () => activeEmployees
      .filter((employee) => employee._id !== selectedEmployeeId && managerCandidateIds.has(employee._id))
      .sort((left, right) => left.name.localeCompare(right.name)),
    [activeEmployees, managerCandidateIds, selectedEmployeeId]
  );

  const deskById = useMemo(() => {
    const map = new Map<string, { desk: FloorplanDesk; levelId: string }>();
    layoutDetail?.layout.levels.forEach((level) => {
      level.desks.forEach((desk) => {
        map.set(desk.id, { desk, levelId: level.id });
      });
    });
    return map;
  }, [layoutDetail]);

  const levelById = useMemo(() => {
    const map = new Map<string, FloorplanLevel>();
    layoutDetail?.layout.levels.forEach((level) => map.set(level.id, level));
    return map;
  }, [layoutDetail]);

  const assignmentsByDeskId = useMemo(() => {
    const map = new Map<string, FloorplanAssignment>();
    localAssignments.forEach((assignment) => {
      map.set(assignment.deskId, assignment);
    });
    return map;
  }, [localAssignments]);

  const assignmentsByEmployeeId = useMemo(() => {
    const map = new Map<string, FloorplanAssignment>();
    localAssignments.forEach((assignment) => {
      map.set(assignment.employeeId, assignment);
    });
    return map;
  }, [localAssignments]);

  const currentLevel = useMemo(
    () => layoutDetail?.layout.levels.find((level) => level.key === selectedLevelKey) || null,
    [layoutDetail, selectedLevelKey]
  );

  const teamLegend = useMemo(() => {
    const uniqueTeams = new Map<string, string>();
    roster.forEach((employee) => {
      if (employee.teamName) {
        uniqueTeams.set(employee.teamName, employee.teamColor);
      }
    });
    return Array.from(uniqueTeams.entries()).sort((left, right) => left[0].localeCompare(right[0]));
  }, [roster]);

  // Every team with its desk count on the floor in view: teams seated here
  // first (most desks first), then the rest, so the picker leads with this floor.
  const teamHighlightOptions = useMemo<TeamHighlightOption[]>(() => {
    const counts = new Map<string, number>();
    if (currentLevel) {
      localAssignments.forEach((assignment) => {
        const teamName = assignment.employee.teamName;
        if (teamName && assignment.desk.levelId === currentLevel.id) {
          counts.set(teamName, (counts.get(teamName) ?? 0) + 1);
        }
      });
    }
    return teamLegend
      .map(([name, color]) => ({ name, color, deskCount: counts.get(name) ?? 0 }))
      .sort((left, right) => right.deskCount - left.deskCount || left.name.localeCompare(right.name));
  }, [currentLevel, localAssignments, teamLegend]);

  // Teams seated on the floor in view, in their colors, for the legend's team chips.
  const floorTeams = useMemo(
    () => teamHighlightOptions
      .filter((team) => team.deskCount > 0)
      .map(({ name, color, deskCount }) => ({ name, color: color ?? '', deskCount })),
    [teamHighlightOptions]
  );

  const levelAssignedCount = useMemo(
    () => (currentLevel ? currentLevel.desks.filter((desk) => assignmentsByDeskId.has(desk.id)).length : 0),
    [assignmentsByDeskId, currentLevel]
  );

  // Background polls usually finish well inside 300ms; only a slow refresh shows the bar.
  const showRefreshing = useDelayedFlag(Boolean(layoutLoading && layoutDetail), 300);

  const selectedDesk = selectedDeskId ? deskById.get(selectedDeskId)?.desk || null : null;
  const selectedDeskAssignment = selectedDeskId ? assignmentsByDeskId.get(selectedDeskId) || null : null;
  const contextMenuAssignment = contextMenu ? assignmentsByDeskId.get(contextMenu.deskId) || null : null;
  const selectedEmployee = selectedEmployeeId
    ? rosterById.get(selectedEmployeeId) || localAssignments.find((assignment) => assignment.employeeId === selectedEmployeeId)?.employee || null
    : null;
  const selectedEmployeeAssignment = selectedEmployeeId
    ? localAssignments.find((assignment) => assignment.employeeId === selectedEmployeeId) || null
    : null;
  // Count people, not desks: a desk held by someone no longer on the roster must not hide an unseated person.
  const activeUnassignedCount = useMemo(
    () => roster.filter((employee) => !assignmentsByEmployeeId.has(employee.id)).length,
    [assignmentsByEmployeeId, roster]
  );
  const isEmployeeReadOnly = profile?.role === 'employee';
  const isManagerView = isManagerLike(profile?.role);
  const canManageVersions = profile?.role === 'admin';
  const canEditPlacement = profile?.role === 'admin';
  const canAssignAnyEmployee = !isEmployeeReadOnly && roster.some((employee) => employee.canEdit);

  /**
   * Put the canvas back on the server's seating after a failed batch change.
   * An identical poll keeps the same data reference, so the reset effect
   * wouldn't fire on its own; restore the latest snapshot directly. If the
   * refetch did bring new data, the reset effect applies it right after.
   */
  const resyncFromServer = async () => {
    try {
      await refetchLayout();
    } catch {
      // A failed refresh shows the page's "couldn't refresh" warning.
    }
    setLocalAssignments(serverAssignmentsRef.current);
  };

  const handleEmployeeDragStart = (
    event: React.DragEvent,
    employee: FloorplanEmployee,
    sourceDeskId?: string
  ) => {
    if (!employee.canEdit) {
      event.preventDefault();
      return;
    }

    if (sourceDeskId && selectedGroup?.deskIds.has(sourceDeskId)) {
      event.dataTransfer.setData('application/x-attendance-floorplan-group', selectedGroup.teamName);
      event.dataTransfer.effectAllowed = 'move';
      return;
    }

    event.dataTransfer.setData('application/x-attendance-floorplan-employee', employee.id);
    event.dataTransfer.effectAllowed = 'move';
  };

  const runAssignmentMutation = async (deskId: string, employeeId: string): Promise<AssignmentResult> => {
    if (!selectedLayoutId || !selectedVersionId) {
      return { ok: false, message: 'Select a seating version before assigning a desk.' };
    }

    const employee = rosterById.get(employeeId);
    if (!employee) {
      return { ok: false, message: 'This employee is no longer available. Refresh the roster and try again.' };
    }

    if (!employee.canEdit) {
      setFeedback({
        type: 'error',
        message: 'You can only move employees assigned to you.',
      });
      return { ok: false, message: 'You can only move employees assigned to you.' };
    }

    const previousAssignments = localAssignments;
    const previousAssignment = assignmentsByEmployeeId.get(employeeId);
    const destinationAssignment = assignmentsByDeskId.get(deskId);
    const optimisticAssignments = applyAssignmentOptimistically(localAssignments, deskById, deskId, employee);
    const pendingIds = new Set<string>([deskId]);
    if (previousAssignment?.deskId) {
      pendingIds.add(previousAssignment.deskId);
    }
    if (destinationAssignment?.deskId) {
      pendingIds.add(destinationAssignment.deskId);
    }

    setLocalAssignments(optimisticAssignments);
    setPendingDeskIds(pendingIds);

    try {
      await saveAssignment({
        layoutId: selectedLayoutId,
        versionId: selectedVersionId,
        deskId,
        employeeId,
      }).unwrap();

      ignoreRealtimeUntilRef.current = Date.now() + 1800;
      setPendingDeskIds(new Set());
      setFeedback({
        type: 'success',
        message: 'Desk assignment saved.',
      });
      return { ok: true };
    } catch (error: any) {
      setLocalAssignments(previousAssignments);
      setPendingDeskIds(new Set());
      const message = error?.data?.error || 'Couldn’t save the desk assignment. Try again in a minute.';
      setFeedback({ type: 'error', message });
      return { ok: false, message };
    }
  };

  const handleDeskDrop = async (deskId: string, employeeId: string) => {
    await runAssignmentMutation(deskId, employeeId);
  };

  const handleClearAssignment = async (deskId: string): Promise<AssignmentResult> => {
    if (!selectedLayoutId || !selectedVersionId) {
      return { ok: false, message: 'Select a seating version before clearing a desk.' };
    }

    const assignment = assignmentsByDeskId.get(deskId);
    if (assignment && !assignment.employee.canEdit) {
      setFeedback({
        type: 'error',
        message: 'You can only clear desk assignments for employees assigned to you.',
      });
      return { ok: false, message: 'You can only clear desk assignments for employees assigned to you.' };
    }

    const previousAssignments = localAssignments;
    setLocalAssignments((current) => current.filter((assignment) => assignment.deskId !== deskId));
    setPendingDeskIds(new Set([deskId]));

    try {
      await clearAssignment({
        layoutId: selectedLayoutId,
        versionId: selectedVersionId,
        deskId,
      }).unwrap();

      ignoreRealtimeUntilRef.current = Date.now() + 1800;
      setPendingDeskIds(new Set());
      setFeedback({
        type: 'success',
        message: 'Desk cleared.',
      });
      return { ok: true };
    } catch (error: any) {
      setLocalAssignments(previousAssignments);
      setPendingDeskIds(new Set());
      const message = error?.data?.error || 'Couldn’t clear the desk. Try again in a minute.';
      setFeedback({ type: 'error', message });
      return { ok: false, message };
    }
  };

  const handleCreateVersion = async (payload: {
    name: string;
    description?: string;
    cloneFromVersionId?: string;
  }) => {
    if (!canManageVersions) {
      setFeedback({
        type: 'error',
        message: 'Only admins can create seating versions.',
      });
      return;
    }

    if (!selectedLayoutId) {
      return;
    }

    try {
      const version = await createVersion({
        layoutId: selectedLayoutId,
        ...payload,
      }).unwrap();
      setSelectedVersionId(version.id);
      setDeskDialogOpen(false);
      setFeedback({
        type: 'success',
        message: 'Seating version created.',
      });
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t create the seating version. Try again in a minute.',
      });
    }
  };

  const handleActivateVersion = async (versionId: string) => {
    if (!canManageVersions) {
      setFeedback({
        type: 'error',
        message: 'Only admins can activate seating versions.',
      });
      return;
    }

    if (!selectedLayoutId) {
      return;
    }

    try {
      await activateVersion({ versionId, layoutId: selectedLayoutId }).unwrap();
      setFeedback({
        type: 'success',
        message: 'Active seating version updated.',
      });
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t activate the seating version. Try again in a minute.',
      });
    }
  };

  const handleLocateEmployee = (employee: FloorplanEmployee) => {
    const assignment = assignmentsByEmployeeId.get(employee.id);

    if (!assignment) {
      setFeedback({
        type: 'info',
        message: `${employee.name} doesn’t have a desk yet. Drag them from the roster onto an open desk to seat them.`,
      });
      return;
    }

    const level = levelById.get(assignment.desk.levelId);
    if (level) {
      setSelectedLevelKey(level.key);
    }

    setSelectedDeskId(assignment.deskId);
    setHighlightedDesk({
      deskId: assignment.deskId,
      nonce: Date.now(),
    });
  };

  /** Search result: jump to their desk, or open their record when they don't have one yet. */
  const handleFindPerson = (person: FloorplanEmployee) => {
    if (!layoutDetail) {
      // Desks aren't known yet, so "no desk" can't be told apart from "not loaded".
      setFeedback({
        type: 'info',
        message: `Desk assignments are still loading. Search for ${person.name} again in a moment.`,
      });
      return;
    }
    if (assignmentsByEmployeeId.has(person.id)) {
      handleLocateEmployee(person);
      return;
    }
    setSelectedEmployeeId(person.id);
    setEmployeeDetailOpen(true);
  };

  const handleSavePlacement = async (payload: PlacementUpdatePayload) => {
    if (!selectedEmployee) {
      throw new Error('Select an employee before saving changes.');
    }

    if (!canEditPlacement) {
      throw new Error('Only admins can update employee placement from the floorplan.');
    }

    const resolvedManagerEmployeeId = resolveManagerEmployeeId(payload, employeeHierarchy);

    if (resolvedManagerEmployeeId === selectedEmployee.id) {
      throw new Error('An employee cannot report to themselves.');
    }

    if (resolvedManagerEmployeeId && isEmployeeDescendant(
      selectedEmployee.id,
      resolvedManagerEmployeeId,
      employeeHierarchy
    )) {
      throw new Error('Cannot move a manager under one of their own reports.');
    }

    try {
      await updateEmployee({
        id: selectedEmployee.id,
        employee: {
          managerName: payload.managerName,
          managerUserId: payload.managerUserId,
          teamName: payload.teamName,
          department: payload.department,
        },
      }).unwrap();

      await refetchRoster();
      await refetchLayout();
      setEmployeeDetailOpen(false);
      setFeedback({
        type: 'success',
        message: `Updated ${selectedEmployee.name}'s placement.`,
      });
    } catch (error: any) {
      // The dialog owns the draft and its error so a failed save stays editable.
      throw new Error(error?.data?.error || 'Couldn’t update this placement. Your changes are still here; try again in a minute.');
    }
  };

  const handleDeskContextMenu = (deskId: string, position: { x: number; y: number }) => {
    setSelectedDeskId(deskId);
    setContextMenu({
      deskId,
      x: position.x,
      y: position.y,
    });
  };

  const handleSelectTeamGroup = () => {
    if (!selectedDeskAssignment?.employee.canEdit) {
      setFeedback({
        type: 'error',
        message: 'You can only move teams assigned to you.',
      });
      return;
    }

    if (!selectedDeskAssignment?.employee.teamName) {
      setFeedback({
        type: 'info',
        message: 'This person isn’t on a team yet, so there’s no team to select.',
      });
      return;
    }

    const teamName = selectedDeskAssignment.employee.teamName;
    const levelId = selectedDeskAssignment.desk.levelId;
    const matchingAssignments = localAssignments.filter((assignment) => (
      assignment.employee.teamName === teamName
      && assignment.desk.levelId === levelId
      && assignment.employee.canEdit
    ));

    if (matchingAssignments.length <= 1) {
      setFeedback({
        type: 'info',
        message: `No other ${teamName} teammates you can move are seated on this floor.`,
      });
      return;
    }

    setSelectedGroup({
      teamName,
      levelId,
      deskIds: new Set(matchingAssignments.map((assignment) => assignment.deskId)),
      count: matchingAssignments.length,
    });
    setFeedback({
      type: 'info',
      message: `${formatCount(matchingAssignments.length, `${teamName} teammate`)} selected. Drag one of their desks to move the group.`,
    });
  };

  const handleGroupDrop = async (targetDeskId: string) => {
    if (!selectedGroup || !selectedVersionId) {
      return;
    }

    const targetDeskEntry = deskById.get(targetDeskId);
    const targetDesk = targetDeskEntry?.desk;
    if (!targetDeskEntry || !targetDesk) {
      return;
    }

    if (targetDeskEntry.levelId !== selectedGroup.levelId) {
      setFeedback({
        type: 'error',
        message: 'Team group moves must stay on the same floor.',
      });
      return;
    }

    try {
      const preview = await previewNearbyTeamMove({
        versionId: selectedVersionId,
        teamName: selectedGroup.teamName,
        targetDeskId,
        sourceLevelId: selectedGroup.levelId,
      }).unwrap();

      if (preview.missingDeskCount > 0) {
        setFeedback({
          type: 'error',
          message: `Not enough nearby desks are open for the full ${selectedGroup.teamName} group.`,
        });
        return;
      }

      if (preview.moves.length === 0) {
        setFeedback({
          type: 'error',
          message: `Couldn’t plan a move for ${selectedGroup.teamName}. Try a different desk.`,
        });
        return;
      }

      const targetDeskLabel = targetDesk ? `desk ${targetDesk.number}` : 'the target desk';
      const confirmed = await confirm({
        title: `Move ${formatCount(preview.moves.length, `${selectedGroup.teamName} teammate`)} near ${targetDeskLabel}?`,
        body: 'Each person moves to an open desk near the one you chose, and their current desks open up.',
        confirmLabel: 'Move teammates',
      });
      if (!confirmed) {
        return;
      }

      const batchAssignments = preview.moves.map((move) => ({
        deskId: move.newDesk.id,
        employeeId: move.employee.id,
      }));
      const pendingIds = new Set<string>(preview.moves.flatMap((move) => [move.oldDesk.id, move.newDesk.id]));
      // Build on the seating as it is now, not as it was when the dialog opened:
      // polling and Realtime may have changed it while the person decided.
      setLocalAssignments((current) => preview.moves.reduce(
        (next, move) => applyAssignmentOptimistically(next, deskById, move.newDesk.id, move.employee),
        current
      ));
      setPendingDeskIds(pendingIds);

      await applyBatchAssignments({
        versionId: selectedVersionId,
        assignments: batchAssignments,
        activityType: 'team_group_move',
        activityDetails: {
          teamName: selectedGroup.teamName,
          levelId: selectedGroup.levelId,
          targetDeskId,
        },
      }).unwrap();

      ignoreRealtimeUntilRef.current = Date.now() + 1800;
      setPendingDeskIds(new Set());
      setSelectedGroup(null);
      setFeedback({
        type: 'success',
        message: `Moved ${preview.moves.length} ${selectedGroup.teamName} teammates nearby.`,
      });
    } catch (error: any) {
      await resyncFromServer();
      setPendingDeskIds(new Set());
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t move the team. Try again in a minute.',
      });
    }
  };

  const handlePreviewSmartAssign = async (params: {
    mode: 'team' | 'all';
    teamName?: string | null;
    levelId?: string | null;
  }) => {
    if (!selectedVersionId) {
      // The dialog can be opened before the layout (and its versions) has loaded; say why nothing happens.
      setFeedback({
        type: 'info',
        message: !layoutDetail
          ? 'Seating is still loading. Preview again in a moment.'
          : canManageVersions
            ? 'There’s no seating version to assign into yet. Create a draft, then preview again.'
            : 'There’s no seating version to assign into yet. Ask an admin to create one.',
      });
      return;
    }

    try {
      const preview = await previewFloorplanAutoAssign({
        versionId: selectedVersionId,
        ...params,
      }).unwrap();
      setSmartAssignPreview(preview);
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t preview seating. Nothing has changed; try again in a minute.',
      });
    }
  };

  const handleApplySmartAssign = async (params: {
    mode: 'team' | 'all';
    teamName?: string | null;
    levelId?: string | null;
  }) => {
    if (!selectedVersionId) {
      return;
    }

    try {
      const result = await applyFloorplanAutoAssign({
        versionId: selectedVersionId,
        ...params,
      }).unwrap();

      ignoreRealtimeUntilRef.current = Date.now() + 1800;
      setSmartAssignPreview(result.preview);
      await refetchLayout();
      await refetchActivity();
      setFeedback({
        type: 'success',
        message: `Placed ${formatCount(result.preview.totalPlaced, 'employee')} at open desks.`,
      });
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t auto-assign desks. Preview the seating again, then retry in a minute.',
      });
    }
  };

  const handleFillRestOfTeamNearby = async () => {
    if (!selectedDeskAssignment?.employee.canEdit) {
      setFeedback({
        type: 'error',
        message: 'You can only place the rest of teams assigned to you.',
      });
      return;
    }

    if (!selectedDeskAssignment?.employee.teamName || !selectedVersionId) {
      return;
    }

    try {
      const preview = await previewFloorplanAutoAssign({
        versionId: selectedVersionId,
        mode: 'team',
        teamName: selectedDeskAssignment.employee.teamName,
        levelId: selectedDeskAssignment.desk.levelId,
      }).unwrap();

      if (preview.totalPlaced === 0) {
        setFeedback({
          type: 'info',
          message: `No ${selectedDeskAssignment.employee.teamName} teammates without a desk can be placed nearby.`,
        });
        return;
      }

      const confirmed = await confirm({
        title: `Place ${formatCount(preview.totalPlaced, `remaining ${selectedDeskAssignment.employee.teamName} teammate`)} on this floor?`,
        body: 'Auto-assign seats them at open desks near their team.',
        confirmLabel: 'Place teammates',
      });
      if (!confirmed) {
        return;
      }

      await handleApplySmartAssign({
        mode: 'team',
        teamName: selectedDeskAssignment.employee.teamName,
        levelId: selectedDeskAssignment.desk.levelId,
      });
    } catch (error: any) {
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t preview team placement. Try again in a minute.',
      });
    }
  };

  const handleOpenDesk = (deskId: string) => {
    setSelectedDeskId(deskId);
    const assignment = assignmentsByDeskId.get(deskId);
    if (assignment) {
      setSelectedEmployeeId(assignment.employeeId);
      setEmployeeDetailOpen(true);
      return;
    }

    if (!canAssignAnyEmployee) {
      return;
    }

    setDeskDialogOpen(true);
  };

  const handleClearCurrentLevel = async () => {
    if (!canManageVersions) {
      setFeedback({
        type: 'error',
        message: 'Only admins can clear an entire floor.',
      });
      return;
    }

    if (!currentLevel || !selectedVersionId || !selectedLayoutId) {
      return;
    }

    const level = currentLevel;
    const levelDeskIds = new Set(level.desks.map((desk) => desk.id));
    const occupiedDeskIds = (assignments: FloorplanAssignment[]) => assignments
      .filter((assignment) => levelDeskIds.has(assignment.deskId))
      .map((assignment) => assignment.deskId);
    const affectedCount = occupiedDeskIds(localAssignments).length;

    if (affectedCount === 0) {
      setFeedback({
        type: 'info',
        message: `No one has a desk on ${level.name} yet, so there’s nothing to clear.`,
      });
      return;
    }

    const confirmed = await confirm({
      title: `Clear ${formatCount(affectedCount, 'desk assignment')} on ${level.name}?`,
      body: 'Everyone on this floor loses their desk in this seating version. Other seating versions are not affected.',
      confirmLabel: `Clear ${formatCount(affectedCount, 'assignment')}`,
      destructive: true,
    });
    if (!confirmed) {
      return;
    }

    // Recount from the seating as it is now; it may have changed while the dialog was open.
    setLocalAssignments((current) => current.filter((assignment) => assignment.desk.levelId !== level.id));
    setPendingDeskIds(new Set(occupiedDeskIds(localAssignmentsRef.current)));
    setIsClearingLevel(true);

    try {
      const result = await clearLevelAssignments({
        versionId: selectedVersionId,
        levelId: level.id,
        layoutId: selectedLayoutId,
      }).unwrap();

      ignoreRealtimeUntilRef.current = Date.now() + 1800;
      setPendingDeskIds(new Set());
      setFeedback({
        type: 'success',
        message: `Cleared ${formatCount(result.clearedCount, 'desk')} on ${level.name}.`,
      });
    } catch (error: any) {
      await resyncFromServer();
      setPendingDeskIds(new Set());
      setFeedback({
        type: 'error',
        message: error?.data?.error || 'Couldn’t clear this floor. Try again in a minute.',
      });
    } finally {
      setIsClearingLevel(false);
    }
  };

  const handleExportCsv = () => {
    if (!layoutDetail) {
      return;
    }

    createDownload(
      `attendance-floorplan-${layoutDetail.layout.slug || 'layout'}.csv`,
      buildCsvExport(layoutDetail, localAssignments),
      'text/csv;charset=utf-8'
    );
    setExportAnchorEl(null);
  };

  const handleExportJson = () => {
    if (!layoutDetail) {
      return;
    }

    createDownload(
      `attendance-floorplan-${layoutDetail.layout.slug || 'layout'}.json`,
      JSON.stringify(
        {
          exportedAt: new Date().toISOString(),
          layout: layoutDetail.layout,
          selectedVersionId,
          assignments: localAssignments,
        },
        null,
        2
      ),
      'application/json;charset=utf-8'
    );
    setExportAnchorEl(null);
  };

  const handlePrint = () => {
    if (!layoutDetail) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1280,height=900');
    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildPrintHtml(layoutDetail, localAssignments));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleLevelDragOver = (levelKey: string) => (event: React.DragEvent) => {
    event.preventDefault();

    if (selectedLevelKey === levelKey || hoverLevelKeyRef.current === levelKey) {
      return;
    }

    if (hoverSwitchTimerRef.current) {
      window.clearTimeout(hoverSwitchTimerRef.current);
    }

    hoverLevelKeyRef.current = levelKey;
    hoverSwitchTimerRef.current = window.setTimeout(() => {
      setSelectedLevelKey(levelKey);
      hoverLevelKeyRef.current = null;
      hoverSwitchTimerRef.current = null;
    }, 350);
  };

  const clearHoverLevelSwitch = () => {
    if (hoverSwitchTimerRef.current) {
      window.clearTimeout(hoverSwitchTimerRef.current);
      hoverSwitchTimerRef.current = null;
    }
    hoverLevelKeyRef.current = null;
  };

  const openLayoutEditor = () => {
    if (currentLevel) setEditingLevel(JSON.parse(JSON.stringify(currentLevel)));
  };

  if (layoutsLoading || rosterLoading) {
    return <PageLoading label="Loading the floor plan…" variant="canvas" />;
  }

  if (layoutsError || rosterError) {
    return (
      <PageError
        title="Couldn’t load the floor plan"
        description="The server didn’t respond. Try again in a minute."
        onRetry={() => { refetchLayouts(); refetchRoster(); }}
      />
    );
  }

  if (!layouts.length) {
    return (
      <Box>
        <PageHeader overline="Workspace" title="Floor plan" />
        <SectionCard>
          <EmptyState
            title="No floor plan yet"
            body="The office layout appears here once an admin sets it up."
            action={<Button variant="outlined" onClick={() => { refetchLayouts(); }}>Check again</Button>}
          />
        </SectionCard>
      </Box>
    );
  }

  const isAdmin = profile?.role === 'admin';
  const levels = layoutDetail?.layout.levels || [];
  // Floors, versions and assignments all arrive with the layout (it can take several
  // seconds). Until it has loaded once, regions that depend on it show skeletons,
  // never empty-state copy or counts derived from the not-yet-loaded empty lists.
  const seatingLoading = !layoutDetail;
  const selectedVersion = layoutDetail?.versions.find((version) => version.id === selectedVersionId) ?? null;
  const activeVersion = layoutDetail?.versions.find((version) => version.isActive) ?? null;
  const presentAreaTypes = currentLevel
    ? Array.from(new Set(currentLevel.areas.map((area) => area.type)))
        .sort((left, right) => {
          const order = FLOORPLAN_AREA_TYPES as readonly string[];
          const rank = (type: string) => (order.indexOf(type) < 0 ? order.length : order.indexOf(type));
          return rank(left) - rank(right);
        })
    : [];
  const selectedDeskLevel = selectedDeskId ? levelById.get(deskById.get(selectedDeskId)?.levelId ?? '') ?? null : null;
  const highlightedTeamOption = highlightedTeam
    ? teamHighlightOptions.find((team) => team.name === highlightedTeam) ?? { name: highlightedTeam, deskCount: 0 }
    : null;
  const deskChangesSaving = assignmentSaving || assignmentClearing || pendingDeskIds.size > 0 || isClearingLevel;

  const editLayoutBlockedReason = seatingLoading
    ? 'Wait for the layout to finish loading.'
    : !currentLevel
      ? 'Choose a floor to edit its layout.'
      : !currentLevel.updatedAt
        ? 'Refresh the page to load the latest floor revision before editing.'
        : layoutLoading
          ? 'Wait for the latest layout to finish loading.'
          : deskChangesSaving
            ? 'Wait for desk changes to finish saving.'
            : versionCreating || versionActivating
              ? 'Wait for the seating version change to finish.'
              : null;

  const clearFloorBlockedReason = seatingLoading
    ? 'Wait for the layout to finish loading'
    : !currentLevel
      ? 'Choose a floor first'
      : deskChangesSaving
        ? 'Wait for desk changes to finish saving'
        : undefined;
  const contextEditBlockedReason = contextMenuAssignment && !contextMenuAssignment.employee.canEdit
    ? (isEmployeeReadOnly ? 'The floor plan is view only for you' : 'Only their manager or an admin can change this')
    : undefined;
  const contextTeamBlockedReason = contextEditBlockedReason
    ?? (contextMenuAssignment && !contextMenuAssignment.employee.teamName ? 'They aren’t on a team yet' : undefined);

  const describePerson = (person: FloorplanEmployee) => {
    // Until assignments load, describe the person, not a desk status that isn't known yet.
    if (seatingLoading) return person.teamName || person.department?.name || person.email;
    const assignment = assignmentsByEmployeeId.get(person.id);
    if (!assignment) return 'No desk yet';
    return [`Desk ${assignment.desk.number}`, levelById.get(assignment.desk.levelId)?.name].filter(Boolean).join(' · ');
  };

  // Freshness says when the seating was last checked (every successful poll),
  // turns to a warning while refreshes fail, and says "Live" only while the
  // Realtime channel is actually subscribed.
  const lastChecked = layoutCheckedAt ? formatTime(layoutCheckedAt) : null;
  const freshnessDot = (color: string) => (
    <Box component="span" aria-hidden="true" sx={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', bgcolor: color }} />
  );
  let freshness: React.ReactNode = null;
  if (layoutError && layoutDetail) {
    freshness = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: sbg.status.warning.text }}>
        {freshnessDot(sbg.status.warning.icon)}
        {lastChecked ? `Couldn’t refresh, last updated ${lastChecked}` : 'Couldn’t refresh'}
      </Box>
    );
  } else if (lastChecked && realtimeLive) {
    freshness = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {freshnessDot(sbg.status.success.icon)}
        {liveLabel === 'Synced' ? 'Live, synced just now' : `Live, updated ${lastChecked}`}
      </Box>
    );
  } else if (lastChecked) {
    freshness = `Updated ${lastChecked}`;
  }

  let canvasBody: React.ReactNode;
  if (!layoutDetail) {
    canvasBody = layoutError ? (
      <ErrorState
        title="Couldn’t load this layout"
        body="The server didn’t respond. Try again in a minute."
        onRetry={() => { refetchLayout(); }}
      />
    ) : (
      <CanvasSkeleton />
    );
  } else if (!currentLevel) {
    canvasBody = (
      <EmptyState
        title="No floors in this layout yet"
        body={isAdmin ? 'Floors appear here once they are added to this layout.' : 'Floors appear here once an admin adds them to this layout.'}
      />
    );
  } else {
    canvasBody = (
      <>
        <FloorCanvas
          level={currentLevel}
          zoom={zoom}
          assignmentsByDeskId={assignmentsByDeskId}
          autoCenterKey={currentLevel ? `${currentLevel.id}:${zoom}` : null}
          selectedDeskId={selectedDeskId}
          selectedGroupDeskIds={selectedGroup?.deskIds}
          highlightedTeam={highlightedTeam}
          highlightedDesk={highlightedDesk}
          pendingDeskIds={pendingDeskIds}
          onDragStart={handleEmployeeDragStart}
          onDeskDrop={handleDeskDrop}
          onGroupDrop={handleGroupDrop}
          onDeskClick={handleOpenDesk}
          onDeskContextMenu={handleDeskContextMenu}
          onZoomChange={setZoom}
        />
        {currentLevel.desks.length === 0 && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', p: 2, pointerEvents: 'none' }}>
            <Box
              sx={{
                pointerEvents: 'auto',
                maxWidth: 380,
                bgcolor: sbg.bg.raised,
                border: `1px solid ${sbg.border.default}`,
                borderRadius: sbg.radius.lg,
                boxShadow: sbg.shadow.md,
              }}
            >
              <EmptyState
                compact
                title={`No desks on ${currentLevel.name} yet`}
                body={isAdmin ? 'Add desks in the layout editor, then drag people onto them.' : 'Desks appear here once an admin draws this floor.'}
                action={isAdmin ? (
                  <Tooltip title={editLayoutBlockedReason ?? ''}>
                    <span>
                      <Button variant="outlined" onClick={openLayoutEditor} disabled={Boolean(editLayoutBlockedReason)}>
                        Open layout editor
                      </Button>
                    </span>
                  </Tooltip>
                ) : undefined}
              />
            </Box>
          </Box>
        )}
      </>
    );
  }

  return (
    <Box>
      <PageHeader
        overline="Workspace"
        title="Floor plan"
        meta={[
          layoutDetail?.layout.name ?? layouts.find((layout) => layout.id === selectedLayoutId)?.name,
          currentLevel?.name,
          freshness,
        ]}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<Download />}
              endIcon={<ChevronDown />}
              aria-haspopup="menu"
              aria-expanded={Boolean(exportAnchorEl)}
              onClick={(event) => setExportAnchorEl(event.currentTarget)}
            >
              Export
            </Button>
            {isAdmin && (
              <Tooltip title={editLayoutBlockedReason ?? 'Add and arrange desks, offices, walls and doors'}>
                <span>
                  {/* aria-disabled (not disabled) keeps it focusable, so keyboard users reach the reason. */}
                  <Button
                    variant="outlined"
                    startIcon={<PencilRuler />}
                    aria-disabled={editLayoutBlockedReason ? true : undefined}
                    aria-describedby={editLayoutBlockedReason ? 'floorplan-edit-layout-reason' : undefined}
                    className={editLayoutBlockedReason ? 'Mui-disabled' : undefined}
                    onClick={() => {
                      if (!editLayoutBlockedReason) openLayoutEditor();
                    }}
                  >
                    Edit layout
                  </Button>
                  {editLayoutBlockedReason && (
                    <span id="floorplan-edit-layout-reason" className="sbg-sr-only">{editLayoutBlockedReason}</span>
                  )}
                </span>
              </Tooltip>
            )}
            {canAssignAnyEmployee && (
              <Button variant="contained" startIcon={<Armchair />} onClick={() => setSmartAssignOpen(true)}>
                Auto-assign desks
              </Button>
            )}
            <Tooltip title="More floor plan actions">
              <IconButton
                aria-label="More floor plan actions"
                aria-haspopup="menu"
                aria-expanded={Boolean(toolsAnchorEl)}
                onClick={(event) => setToolsAnchorEl(event.currentTarget)}
              >
                <Ellipsis />
              </IconButton>
            </Tooltip>
          </>
        }
      />

      <Stack spacing={2} sx={{ mb: (isManagerView || isEmployeeReadOnly || (layoutError && layoutDetail)) ? 3 : 0 }}>
        {isManagerView && (
          <Alert severity="info" role="note">
            You can view the whole floor. You can move only the people who report to you.
          </Alert>
        )}

        {isEmployeeReadOnly && (
          <Alert severity="info" role="note">
            This floor plan is view only. Ask an admin if your desk needs to change.
          </Alert>
        )}

        {layoutError && layoutDetail && (
          <Alert
            severity="warning"
            action={<Button color="inherit" size="small" onClick={() => { refetchLayout(); }}>Try again</Button>}
          >
            Couldn’t refresh the floor plan. You’re seeing the last loaded seating, so recent changes may be missing.
          </Alert>
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 320px', xl: 'minmax(0, 1fr) 344px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Box
            component="section"
            aria-label="Seating map"
            sx={{
              bgcolor: sbg.bg.surface,
              border: `1px solid ${sbg.border.default}`,
              borderRadius: sbg.radius.card,
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${sbg.border.subtle}`,
              }}
            >
              {layouts.length > 1 && (
                <TextField
                  select
                  value={selectedLayoutId}
                  inputProps={{ 'aria-label': 'Layout' }}
                  SelectProps={{
                    renderValue: (value) => `Layout: ${layouts.find((layout) => layout.id === value)?.name ?? '—'}`,
                  }}
                  onChange={(event) => {
                    setSelectedLayoutId(event.target.value);
                    setSelectedVersionId(null);
                    setSelectedLevelKey('');
                    setSelectedDeskId(null);
                    setDeskDialogOpen(false);
                  }}
                  sx={{ minWidth: 200, width: { xs: '100%', sm: 'auto' }, '& .MuiOutlinedInput-root': { height: TOOLBAR_CONTROL_HEIGHT } }}
                >
                  {layouts.map((layout) => (
                    <MenuItem key={layout.id} value={layout.id}>
                      {layout.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {levels.length > 1 ? (
                <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={selectedLevelKey}
                    onChange={(_event, value: string | null) => {
                      if (value) setSelectedLevelKey(value);
                    }}
                    aria-label="Floor"
                  >
                    {levels.map((level) => (
                      <ToggleButton
                        key={level.id}
                        value={level.key}
                        onDragOver={handleLevelDragOver(level.key)}
                        onDragLeave={clearHoverLevelSwitch}
                        onDrop={clearHoverLevelSwitch}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {level.name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              ) : currentLevel ? (
                <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
                  {currentLevel.name}
                </Typography>
              ) : null}

              <PersonSearch people={roster} describe={describePerson} onSelect={handleFindPerson} />

              <Box sx={{ ml: { sm: 'auto' }, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                {/* Its desk counts come from the layout, so it waits for it. */}
                {!seatingLoading && teamHighlightOptions.length > 0 && (
                  <TeamHighlight teams={teamHighlightOptions} value={highlightedTeam} onChange={setHighlightedTeam} />
                )}
                <ZoomControls zoom={zoom} onChange={setZoom} />
              </Box>
            </Box>

            {selectedGroup && (
              <Box
                role="status"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  bgcolor: sbg.bg.selected,
                  borderBottom: `1px solid ${sbg.border.subtle}`,
                  color: sbg.text.primary,
                }}
              >
                <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: sbg.text.accent }}>
                  <Users size={16} />
                </Box>
                <Typography variant="body1" sx={{ flex: 1, minWidth: 200 }}>
                  <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatCount(selectedGroup.count, 'desk')} selected
                  </Box>
                  {` · ${selectedGroup.teamName}. Drag any selected desk to move the group nearby.`}
                </Typography>
                <Typography variant="caption" sx={{ color: sbg.text.secondary, display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', gap: 0.5 }}>
                  <Kbd>Esc</Kbd> to clear
                </Typography>
                <Button variant="text" size="small" onClick={() => setSelectedGroup(null)}>
                  Clear selection
                </Button>
              </Box>
            )}

            <Box sx={{ position: 'relative' }}>
              {showRefreshing && (
                <LinearProgress
                  aria-label="Refreshing the floor plan"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1,
                    height: 2,
                    bgcolor: 'transparent',
                    '& .MuiLinearProgress-bar': { bgcolor: sbg.action.primary },
                  }}
                />
              )}
              {canvasBody}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${sbg.border.subtle}`,
              }}
            >
              <CanvasLegend
                areaTypes={presentAreaTypes}
                teams={floorTeams}
                highlightedTeam={highlightedTeamOption}
                onTeamToggle={(teamName) => setHighlightedTeam((current) => (current === teamName ? null : teamName))}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  component="p"
                  sx={{ color: sbg.text.secondary, display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', gap: 0.5 }}
                >
                  Pinch or <Kbd>Ctrl</Kbd> + scroll to zoom · drag to pan
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<History />}
                  aria-expanded={showActivity}
                  // The history region only exists while it's expanded (unmountOnExit).
                  aria-controls={showActivity ? 'floorplan-history' : undefined}
                  onClick={() => setShowActivity((current) => !current)}
                >
                  {showActivity ? 'Hide history' : 'Show history'}
                </Button>
              </Box>
            </Box>
          </Box>

          <Collapse
            in={showActivity}
            unmountOnExit
            onEntered={() => {
              // The card opens below a tall canvas: bring it into view and move focus to it.
              historyRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
              historyRef.current?.focus({ preventScroll: true });
            }}
          >
            <Box
              ref={historyRef}
              id="floorplan-history"
              role="region"
              aria-label="Floor plan history"
              tabIndex={-1}
              sx={{ borderRadius: sbg.radius.card, outline: 'none', '&:focus-visible': focusRing }}
            >
              <ActivityFeed
                entries={activity}
                levels={levels}
                loading={activityLoading}
                error={Boolean(activityError)}
                onRetry={() => { refetchActivity(); }}
              />
            </Box>
          </Collapse>
        </Stack>

        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <SelectionPanel
            desk={selectedDesk}
            deskLevelName={selectedDeskLevel?.name ?? null}
            assignment={selectedDeskAssignment}
            pending={selectedDeskId ? pendingDeskIds.has(selectedDeskId) : false}
            canAssign={canAssignAnyEmployee}
            canEditPlacement={canEditPlacement}
            level={currentLevel}
            levelAssignedCount={levelAssignedCount}
            unassignedPeopleCount={activeUnassignedCount}
            rosterCount={roster.length}
            loading={seatingLoading}
            onOpenPerson={() => {
              if (selectedDeskAssignment) {
                setSelectedEmployeeId(selectedDeskAssignment.employeeId);
                setEmployeeDetailOpen(true);
              }
            }}
            onAssignDesk={() => setDeskDialogOpen(true)}
            onShowOnFloor={() => {
              if (selectedDeskAssignment) {
                handleLocateEmployee(selectedDeskAssignment.employee);
              }
            }}
            onClearSelection={() => setSelectedDeskId(null)}
          />

          <VersionSelector
            versions={layoutDetail?.versions || []}
            selectedVersionId={selectedVersionId}
            isCreating={versionCreating}
            isActivating={versionActivating}
            canManageVersions={canManageVersions}
            loading={seatingLoading}
            onSelect={setSelectedVersionId}
            onCreate={handleCreateVersion}
            onActivate={handleActivateVersion}
          />

          <EmployeeRoster
            employees={roster}
            assignmentsByEmployeeId={assignmentsByEmployeeId}
            assignmentsLoading={seatingLoading}
            levels={levels}
            canAssign={canAssignAnyEmployee}
            employeesHref={isAdmin ? '/employees' : undefined}
            onDragStart={handleEmployeeDragStart}
            onLocateEmployee={handleLocateEmployee}
            disabled={!selectedVersionId || assignmentSaving || assignmentClearing || isEmployeeReadOnly}
          />
        </Stack>
      </Box>

      {editingLevel && profile?.role === 'admin' && (
        <FloorplanLayoutEditor
          level={layoutDetail?.layout.levels.find(level => level.id === editingLevel.id) || editingLevel}
          assignmentsByDeskId={assignmentsByDeskId}
          onSave={handleSaveGeometry}
          onClose={() => setEditingLevel(null)}
        />
      )}

      <DeskAssignmentDialog
        open={deskDialogOpen}
        desk={selectedDesk}
        assignment={selectedDeskAssignment}
        employees={roster}
        assignmentsByEmployeeId={assignmentsByEmployeeId}
        busy={assignmentSaving || assignmentClearing}
        levels={levels}
        onClose={() => setDeskDialogOpen(false)}
        onAssign={async (employeeId) => {
          if (!selectedDeskId) {
            throw new Error('Choose a desk before assigning an employee.');
          }

          const result = await runAssignmentMutation(selectedDeskId, employeeId);
          if (!result.ok) throw new Error(result.message);
          setDeskDialogOpen(false);
        }}
        onClear={async () => {
          if (!selectedDeskId) {
            throw new Error('Choose a desk before clearing its assignment.');
          }

          const result = await handleClearAssignment(selectedDeskId);
          if (!result.ok) throw new Error(result.message);
          setDeskDialogOpen(false);
        }}
      />

      <EmployeeDetailDialog
        open={employeeDetailOpen}
        employee={selectedEmployee}
        assignment={selectedEmployeeAssignment}
        managerCandidates={managerCandidates}
        departments={departments}
        canEditPlacement={canEditPlacement}
        placementSaving={updatingEmployeePlacement}
        levels={levels}
        onClose={() => setEmployeeDetailOpen(false)}
        onLocate={() => {
          if (selectedEmployee) {
            handleLocateEmployee(selectedEmployee);
          }
          setEmployeeDetailOpen(false);
        }}
        onChangeDesk={() => {
          if (selectedEmployeeAssignment) {
            setSelectedDeskId(selectedEmployeeAssignment.deskId);
          }
          setEmployeeDetailOpen(false);
          setDeskDialogOpen(true);
        }}
        onRemove={async () => {
          if (!selectedEmployeeAssignment) {
            throw new Error('This employee no longer has a desk assignment.');
          }
          const result = await handleClearAssignment(selectedEmployeeAssignment.deskId);
          if (!result.ok) throw new Error(result.message);
          setEmployeeDetailOpen(false);
        }}
        onSavePlacement={handleSavePlacement}
      />

      <StatsDialog
        open={statsOpen}
        levels={levels}
        assignments={localAssignments}
        roster={roster}
        versionName={selectedVersion?.name ?? null}
        isActiveVersion={selectedVersion ? selectedVersion.isActive : undefined}
        activeVersionName={activeVersion?.name ?? null}
        onClose={() => setStatsOpen(false)}
      />

      <SmartAssignDialog
        open={smartAssignOpen}
        teams={teamLegend.map(([teamName]) => teamName)}
        levels={levels}
        preview={smartAssignPreview}
        loading={previewingSmartAssign}
        applying={applyingSmartAssign}
        onClose={() => setSmartAssignOpen(false)}
        onPreview={handlePreviewSmartAssign}
        onApply={handleApplySmartAssign}
      />

      {confirmDialog}

      <Menu anchorEl={exportAnchorEl} open={Boolean(exportAnchorEl)} onClose={() => setExportAnchorEl(null)}>
        <MenuItem onClick={handleExportCsv} disabled={!layoutDetail}>
          <ListItemIcon><FileSpreadsheet /></ListItemIcon>
          <ListItemText>Export CSV</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportJson} disabled={!layoutDetail}>
          <ListItemIcon><FileJson /></ListItemIcon>
          <ListItemText>Export JSON</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setExportAnchorEl(null); handlePrint(); }} disabled={!layoutDetail}>
          <ListItemIcon><Printer /></ListItemIcon>
          <ListItemText>Print floor plan</ListItemText>
        </MenuItem>
      </Menu>

      <Menu anchorEl={toolsAnchorEl} open={Boolean(toolsAnchorEl)} onClose={() => setToolsAnchorEl(null)}>
        <MenuItem disabled={seatingLoading} onClick={() => { setToolsAnchorEl(null); setStatsOpen(true); }}>
          <ListItemIcon><ChartColumn /></ListItemIcon>
          <ListItemText
            primary="View statistics"
            secondary={seatingLoading ? 'Wait for the layout to finish loading' : undefined}
          />
        </MenuItem>
        {canManageVersions && <Divider />}
        {canManageVersions && (
          <MenuItem
            disabled={Boolean(clearFloorBlockedReason)}
            onClick={() => { setToolsAnchorEl(null); handleClearCurrentLevel(); }}
            sx={{ color: sbg.status.danger.text, '& .MuiListItemIcon-root': { color: sbg.status.danger.icon } }}
          >
            <ListItemIcon><Eraser /></ListItemIcon>
            <ListItemText
              primary={currentLevel ? `Clear ${currentLevel.name} assignments…` : 'Clear floor assignments…'}
              secondary={clearFloorBlockedReason}
            />
          </MenuItem>
        )}
      </Menu>

      <Menu
        open={Boolean(contextMenu)}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        {contextMenuAssignment ? (
          [
            <MenuItem
              key="edit-placement"
              onClick={() => {
                setSelectedEmployeeId(contextMenuAssignment.employeeId);
                setEmployeeDetailOpen(true);
                setContextMenu(null);
              }}
            >
              <ListItemIcon><UserPen /></ListItemIcon>
              <ListItemText>{canEditPlacement ? 'Edit placement' : 'View placement'}</ListItemText>
            </MenuItem>,
            <MenuItem
              key="select-team"
              disabled={Boolean(contextTeamBlockedReason)}
              onClick={() => {
                handleSelectTeamGroup();
                setContextMenu(null);
              }}
            >
              <ListItemIcon><Users /></ListItemIcon>
              <ListItemText primary="Select team" secondary={contextTeamBlockedReason} />
            </MenuItem>,
            <MenuItem
              key="fill-team"
              disabled={Boolean(contextTeamBlockedReason)}
              onClick={() => {
                void handleFillRestOfTeamNearby();
                setContextMenu(null);
              }}
            >
              <ListItemIcon><UsersRound /></ListItemIcon>
              <ListItemText primary="Place rest of team nearby" secondary={contextTeamBlockedReason} />
            </MenuItem>,
            <MenuItem
              key="locate"
              onClick={() => {
                handleLocateEmployee(contextMenuAssignment.employee);
                setContextMenu(null);
              }}
            >
              <ListItemIcon><Crosshair /></ListItemIcon>
              <ListItemText>Highlight desk</ListItemText>
            </MenuItem>,
            <Divider key="divider" />,
            <MenuItem
              key="remove"
              disabled={Boolean(contextEditBlockedReason)}
              onClick={() => {
                void handleClearAssignment(contextMenuAssignment.deskId);
                setContextMenu(null);
              }}
              sx={{ color: sbg.status.danger.text, '& .MuiListItemIcon-root': { color: sbg.status.danger.icon } }}
            >
              <ListItemIcon><UserMinus /></ListItemIcon>
              <ListItemText primary="Remove from desk" secondary={contextEditBlockedReason} />
            </MenuItem>,
          ]
        ) : (
          <MenuItem
            disabled={!canAssignAnyEmployee}
            onClick={() => {
              setDeskDialogOpen(true);
              setContextMenu(null);
            }}
          >
            <ListItemIcon><UserPlus /></ListItemIcon>
            <ListItemText
              primary="Assign employee"
              secondary={canAssignAnyEmployee ? undefined : 'Only an admin or a manager can assign desks'}
            />
          </MenuItem>
        )}
      </Menu>

      {feedback && (
        <Snackbar
          key={`${feedback.type}:${feedback.message}`}
          open
          autoHideDuration={feedback.type === 'error' ? null : 5000}
          onClose={(_event, reason) => {
            if (reason !== 'clickaway') setFeedback(null);
          }}
        >
          <Alert
            severity={feedback.type}
            role={feedback.type === 'error' ? 'alert' : 'status'}
            onClose={() => setFeedback(null)}
          >
            {feedback.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default FloorPlan;
